import React, { useEffect, useCallback, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Youtube } from '@tiptap/extension-youtube';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Placeholder } from '@tiptap/extension-placeholder';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { Typography } from '@tiptap/extension-typography';
import { CharacterCount } from '@tiptap/extension-character-count';
import { common, createLowlight } from 'lowlight';

import MenuBar from './MenuBar';
import { SlashMenuExtension } from './SlashMenu';

// Custom extensions
import { FAQ } from './extensions/FAQ';
import { Accordion } from './extensions/Accordion';
import { Alert } from './extensions/Alert';
import { CTA } from './extensions/CTA';
import { Steps } from './extensions/Steps';
import { ProsCons } from './extensions/ProsCons';
import { Stats } from './extensions/Stats';
import { Card } from './extensions/Card';
import { Callout } from './extensions/Callout';
import { Divider } from './extensions/Divider';

import './TipTapEditor.css';
import './extensions/extensions.css';

const lowlight = createLowlight(common);

const TipTapEditor = ({
  content,
  onChange,
  placeholder = 'Start writing or type / for commands...',
  editable = true,
  onFocus,
  onBlur,
  onImageUpload,
  className = '',
}) => {
  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: {
        levels: [1, 2, 3, 4, 5, 6],
      },
      codeBlock: false,
    }),
    Image.configure({
      inline: false,
      allowBase64: true,
      HTMLAttributes: {
        class: 'editor-image',
      },
    }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'editor-link',
        rel: 'noopener noreferrer',
      },
    }),
    Table.configure({
      resizable: true,
      HTMLAttributes: {
        class: 'editor-table',
      },
    }),
    TableRow,
    TableCell,
    TableHeader,
    Youtube.configure({
      width: 640,
      height: 360,
      HTMLAttributes: {
        class: 'editor-youtube',
      },
    }),
    TextStyle,
    Color,
    Highlight.configure({
      multicolor: true,
    }),
    Underline,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Placeholder.configure({
      placeholder,
    }),
    CodeBlockLowlight.configure({
      lowlight,
      HTMLAttributes: {
        class: 'editor-code-block',
      },
    }),
    Typography,
    CharacterCount,
    // Custom extensions
    FAQ,
    Accordion,
    Alert,
    CTA,
    Steps,
    ProsCons,
    Stats,
    Card,
    Callout,
    Divider,
    // Slash command menu
    SlashMenuExtension,
  ], [placeholder]);

  const editor = useEditor({
    extensions,
    content,
    editable,
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      onChange?.(json);
    },
    onFocus: ({ event }) => {
      onFocus?.(event);
    },
    onBlur: ({ event }) => {
      onBlur?.(event);
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor-content',
      },
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer?.files?.length) {
          const files = Array.from(event.dataTransfer.files);
          const images = files.filter(file => file.type.startsWith('image/'));

          if (images.length > 0 && onImageUpload) {
            event.preventDefault();
            images.forEach(async (image) => {
              const url = await onImageUpload(image);
              if (url) {
                const { schema } = view.state;
                const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
                const node = schema.nodes.image.create({ src: url });
                const transaction = view.state.tr.insert(coordinates?.pos || 0, node);
                view.dispatch(transaction);
              }
            });
            return true;
          }
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (items) {
          for (const item of items) {
            if (item.type.startsWith('image/') && onImageUpload) {
              event.preventDefault();
              const file = item.getAsFile();
              if (file) {
                onImageUpload(file).then((url) => {
                  if (url) {
                    const { schema } = view.state;
                    const node = schema.nodes.image.create({ src: url });
                    const transaction = view.state.tr.replaceSelectionWith(node);
                    view.dispatch(transaction);
                  }
                });
              }
              return true;
            }
          }
        }
        return false;
      },
    },
  });

  // Update content when prop changes
  useEffect(() => {
    if (editor && content && JSON.stringify(editor.getJSON()) !== JSON.stringify(content)) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  // Handle image insertion
  const handleImageInsert = useCallback(async (file) => {
    if (onImageUpload && editor) {
      const url = await onImageUpload(file);
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    }
  }, [editor, onImageUpload]);

  // Handle YouTube insertion
  const handleYoutubeInsert = useCallback((url) => {
    if (editor) {
      editor.chain().focus().setYoutubeVideo({ src: url }).run();
    }
  }, [editor]);

  // Handle link insertion
  const handleLinkInsert = useCallback((url, text) => {
    if (editor) {
      if (text) {
        editor.chain().focus().insertContent(`<a href="${url}">${text}</a>`).run();
      } else {
        editor.chain().focus().setLink({ href: url }).run();
      }
    }
  }, [editor]);

  // Handle table insertion
  const handleTableInsert = useCallback((rows = 3, cols = 3) => {
    if (editor) {
      editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    }
  }, [editor]);

  if (!editor) {
    return <div className="tiptap-loading">Loading editor...</div>;
  }

  return (
    <div className={`tiptap-editor ${className}`}>
      <MenuBar
        editor={editor}
        onImageInsert={handleImageInsert}
        onYoutubeInsert={handleYoutubeInsert}
        onLinkInsert={handleLinkInsert}
        onTableInsert={handleTableInsert}
      />
      <EditorContent editor={editor} />
      <div className="tiptap-footer">
        <span className="tiptap-word-count">
          {editor.storage.characterCount.words()} words
        </span>
        <span className="tiptap-char-count">
          {editor.storage.characterCount.characters()} characters
        </span>
      </div>
    </div>
  );
};

export default TipTapEditor;
